import { HttpErrorResponse } from '@angular/common/http';
import { INotesOfAnnualReportDTO, INotesTemplateDTO } from '@modeso/types__aumico-lib-notes-be';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createReducer, createSelector, on } from '@ngrx/store';
import Debug from 'debug';
import * as fromNotesTemplatesActions from '../actions/notesTemplates.actions';
import { ILegalFormAndAccountBasisCombinationCheck } from '../interfaces/ILegalFormAndAccountBasisCombinationCheck.interface';
import { INameExistenceFiltration } from '../interfaces/INameExistenceFiltration.interface';

export const notesTemplatesFeatureKey = 'aumicoNotesTemplatesMicroservice';

const debug = Debug('aumico:aumico-lib-notes-fe:NotesTemplatesReducer');

// State Declarations - START
export interface NotesTemplatesState extends EntityState<INotesTemplateDTO> {
    activeNotesTemplates: string | null;
    notesTemplateError: HttpErrorResponse;
    notesOfAnnualReport: INotesOfAnnualReportDTO;
}

export function selectNotesTemplatesId(a: INotesTemplateDTO): string {
    return a._id;
}

export const adapter: EntityAdapter<INotesTemplateDTO> = createEntityAdapter<INotesTemplateDTO>({
    selectId: selectNotesTemplatesId,
});

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectNotesTemplatesIds = selectIds;

// select the dictionary of notes templates entities
export const selectNotesTemplatesEntities = selectEntities;

// select the array of notes templates
export const selectAllNotesTemplates = selectAll;

// select the total notes templates count
export const selectNotesTemplatesTotal = selectTotal;

export const selectEntity = createSelector(selectEntities, (entities, props) => entities[props.id]);

export const getActiveNotesTemplateId = (state: NotesTemplatesState) => state.activeNotesTemplates;

export const getNotesTemplateError = (state: NotesTemplatesState) => state.notesTemplateError;

export const getNotes = (state: NotesTemplatesState) => state.notesOfAnnualReport;

export const getActiveNotesTemplate = createSelector(
    selectEntities,
    getActiveNotesTemplateId,
    (entities, id) => entities[id],
);

export const legalFormAndAccountBasisExistenceSelector = createSelector(
    selectEntities,
    (entities, props: ILegalFormAndAccountBasisCombinationCheck) => {
        const keys = Object.keys(entities);

        for (const key of keys) {
            const notesTemplate: INotesTemplateDTO = entities[key];
            const accountBasisEquality: boolean = notesTemplate.accountBasis.id === props.accountBasis;

            const legalFormEquality: boolean = notesTemplate.legalForm.id === props.legalForm;

            const isLegalFormAndAccountBasisCombinationExistL: boolean =
                accountBasisEquality &&
                legalFormEquality &&
                notesTemplate._id !== props.id &&
                notesTemplate.owner === props.owner;
            if (isLegalFormAndAccountBasisCombinationExistL) {
                return true;
            }
        }

        return false;
    },
);

export const nameExistenceSelector = createSelector(selectEntities, (entities, props: INameExistenceFiltration) => {
    const keys = Object.keys(entities);

    for (const key of keys) {
        const notesTemplate: INotesTemplateDTO = entities[key];

        if (
            notesTemplate.name === props.name &&
            entities[key]._id !== props.id &&
            notesTemplate.owner === props.owner
        ) {
            return true;
        }
    }

    return false;
});

export const initialNotesTemplatesState: NotesTemplatesState = adapter.getInitialState({
    // additional entity state properties
    activeNotesTemplates: null,
    notesTemplateError: undefined,
    notesOfAnnualReport: undefined,
});

const notesTemplatesReducer = createReducer(
    initialNotesTemplatesState,

    on(fromNotesTemplatesActions.getNotesTemplates, (state) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: undefined,
    })),
    on(fromNotesTemplatesActions.onGetNotesTemplatesSuccessfully, (state, action) => {
        return adapter.setAll(action.payload, {
            ...state,
            activeNotesTemplates: null,
            notesTemplateError: undefined,
            notes: undefined,
        });
    }),
    on(fromNotesTemplatesActions.onGetNotesTemplatesFailed, (state, action) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: action.payload,
    })),

    on(fromNotesTemplatesActions.getNotes, (state) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: undefined,
    })),
    on(fromNotesTemplatesActions.onGetNotesSuccessfully, (state, action) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: undefined,
        notesOfAnnualReport: action.payload,
    })),
    on(fromNotesTemplatesActions.onGetNotesFailed, (state, action) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: action.payload,
    })),
    on(fromNotesTemplatesActions.resetNotes, (state) => ({
        ...state,
        notesTemplateError: undefined,
        notesOfAnnualReport: undefined,
    })),

    on(fromNotesTemplatesActions.createNotesTemplate, (state) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: undefined,
    })),
    on(fromNotesTemplatesActions.onCreateNotesTemplateSuccessfully, (state, action) => {
        return adapter.addOne(action.payload, {
            ...state,
            activeNotesTemplates: null,
            notesTemplateError: undefined,
            notes: undefined,
        });
    }),
    on(fromNotesTemplatesActions.onCreateNotesTemplateFailed, (state, action) => ({
        ...state,
        activeNotesTemplates: null,
        notesTemplateError: action.payload,
    })),

    on(fromNotesTemplatesActions.updateNotesTemplate, (state) => ({ ...state, notesTemplateError: undefined })),
    on(fromNotesTemplatesActions.onUpdateNotesTemplateSuccessfully, (state, action) => {
        return adapter.updateOne(
            { id: action.payload._id, changes: action.payload },
            { ...state, activeNotesTemplates: null, notesTemplateError: undefined, notes: undefined },
        );
    }),
    on(fromNotesTemplatesActions.onUpdateNotesTemplateFailed, (state, action) => ({
        ...state,
        notesTemplateError: action.payload,
    })),

    on(fromNotesTemplatesActions.onSelectNotesTemplate, (state, action) => ({
        ...state,
        activeNotesTemplates: action.payload,
        notesTemplateError: undefined,
        notesOfAnnualReport: undefined,
    })),
);

export function reducer(state: NotesTemplatesState | undefined, action: Action) {
    return notesTemplatesReducer(state, action);
}
